<template>
    <div
        :class="
            cn(
                'google-gradient rounded-lg bg-white focus:ring-z-primary-300 relative flex h-full flex-col border focus:outline-none',

                hasError
                    ? 'border-error-400 ring-error-100 ring-[3px]'
                    : 'border-iron-200',
                gradientClass,
            )
        "
    >
        <template v-if="showBadge">
            <span
                v-if="paid"
                class="gap-1 rounded-md from-amber-50 to-pink-50 px-2 py-0.5 font-medium text-iron-700 top-3 right-3 absolute inline-flex items-center bg-gradient-to-r text-[11px]"
            >
                <PaidFeatureIcon />
                {{ $t("integrations.paid") }}
            </span>
            <span
                v-else-if="paid === false"
                class="rounded-md bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700 top-3 right-3 absolute text-[11px]"
            >
                {{ $t("integrations.free") }}
            </span>
        </template>

        <div class="gap-6 px-6 pt-6 flex flex-grow items-stretch">
            <div class="max-h-[24px]">
                <slot name="logo" />
            </div>

            <div class="flex flex-1 flex-col">
                <h3
                    class="text-lg font-medium text-iron-700 truncate text-left tracking-[-0.01em]"
                >
                    <slot name="name" />
                </h3>

                <p class="text-sm font-medium text-iron-500 text-left">
                    <slot name="identifier" />
                </p>

                <p
                    class="text-sm text-iron-500 mt-[3px] line-clamp-2 text-left tracking-[-0.01em]"
                >
                    <slot name="description" />
                </p>
            </div>
        </div>

        <div class="mt-4">
            <div
                class="rounded-b-lg border-iron-200 bg-iron-25 px-6 py-2 flex justify-end border-t"
                :class="[
                    hasError ? 'pl-3 pr-6 justify-between' : 'px-6 justify-end',
                ]"
            >
                <div v-if="hasError" class="gap-2 flex items-center">
                    <div
                        class="h-6 w-6 bg-error-100 flex items-center justify-center rounded-full"
                    >
                        <CircleAlert
                            :size="16"
                            class="text-error-600"
                        ></CircleAlert>
                    </div>
                    <p class="text-sm font-medium text-error-700">
                        {{ $t("integrations.issue") }}
                    </p>
                </div>
                <slot name="cta" />
                <Button
                    v-if="!selected && !$slots.cta"
                    variant="ghost"
                    size="sm"
                >
                    {{ $t("integrations.learn_more") }}</Button
                >
                <Button v-if="selected" size="sm" variant="ghost">
                    {{ $t("integrations.show_integration") }}
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Button } from "@/components/ui/button";
import PaidFeatureIcon from "@/Pages/Zaptime/App/Atomic/PaidFeatureIcon.vue";
import { useHasSubscription } from "@/composables/useHasSubscription";
import { cn } from "@/lib/utils";
import { usePage } from "@inertiajs/vue3";
import { CircleAlert } from "lucide-vue-next";
import { computed } from "vue";

const { hasSubscription } = useHasSubscription();

const page = usePage<{ onboardingCompleted?: boolean | null }>();
const onboardingCompleted = computed(() => !!page.props.onboardingCompleted);
const showBadge = computed(() => !hasSubscription && onboardingCompleted.value);

interface Props {
    selected?: boolean;
    gradientClass: string;
    hasError?: boolean;
    paid?: boolean;
}

defineProps<Props>();
</script>

<style>
.google-gradient {
    background: linear-gradient(
        291.26deg,
        rgba(79, 233, 121, 0) 70%,
        rgba(79, 233, 121, 0.16) 100%
    );
}

.icloud-gradient {
    background: linear-gradient(
        300deg,
        rgba(255, 55, 43, 0) 80%,
        rgba(255, 55, 43, 0.16) 100%
    );
}

.microsoft-gradient {
    background: linear-gradient(
        300deg,
        rgba(0, 120, 212, 0) 80%,
        rgba(0, 120, 212, 0.16) 100%
    );
}

.microsoft-teams-gradient {
    background: linear-gradient(
        300deg,
        rgba(80, 89, 201, 0) 80%,
        rgba(80, 89, 201, 0.16) 100%
    );
}

.slack-gradient {
    background: linear-gradient(
        300deg,
        rgba(159, 22, 161, 0) 80%,
        rgba(159, 22, 161, 0.16) 100%
    );
}

.ecm-gradient {
    background: linear-gradient(
        300deg,
        rgba(166, 230, 31, 0) 80%,
        rgba(166, 230, 31, 0.16) 100%
    );
}

.close-gradient {
    background: linear-gradient(
        300deg,
        rgba(255, 203, 57, 0) 80%,
        rgba(255, 203, 57, 0.16) 100%
    );
}

.hubspot-gradient {
    background: linear-gradient(
        300deg,
        rgba(255, 122, 89, 0) 80%,
        rgba(255, 122, 89, 0.16) 100%
    );
}

.raynet-gradient {
    background: linear-gradient(
        280deg,
        rgba(23, 190, 198, 0) 80%,
        rgba(23, 190, 198, 0.16) 100%
    );
}

.freelo-gradient {
    background: linear-gradient(
        285.21deg,
        rgba(66, 198, 254, 0) 80%,
        rgba(66, 198, 254, 0.16) 100%
    );
}

.stripe-gradient {
    background: linear-gradient(
        285.21deg,
        rgba(99, 91, 255, 0) 80%,
        rgba(99, 91, 255, 0.16) 100%
    );
}

.mcp-gradient {
    background: linear-gradient(
        285.21deg,
        rgba(124, 58, 237, 0) 80%,
        rgba(124, 58, 237, 0.16) 100%
    );
}

.zaptime-gradient {
    background: linear-gradient(
        285.21deg,
        rgba(255, 141, 71, 0) 80%,
        rgba(207, 2, 131, 0.16) 100%
    );
}
</style>
